interface RecommendedFormatLike {
  formatType?: unknown;
  weight?: unknown;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' ? value as Record<string, unknown> : {};
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => String(item ?? '').trim())
    .filter(Boolean);
}

function asRecommendedFormats(value: unknown): RecommendedFormatLike[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is RecommendedFormatLike => item !== null && typeof item === 'object');
}

export function templateCompatibleFormatTypes(
  template?: unknown,
): string[] {
  const source = asRecord(template);
  const direct = asStringArray(source.compatibleFormatTypes);
  const recommended = asRecommendedFormats(source.recommendedFormats)
    .map((entry) => String(entry.formatType ?? '').trim())
    .filter(Boolean);

  return Array.from(new Set([...direct, ...recommended]));
}

export function isTemplateCompatibleWithFormat(
  template?: unknown,
  format?: unknown,
): boolean {
  if (!template || !format) return true;
  const compatibleTypes = templateCompatibleFormatTypes(template);
  if (compatibleTypes.length === 0) return true;
  const formatSource = asRecord(format);
  const formatType = String(formatSource.formatType ?? formatSource.id ?? '').trim();
  return compatibleTypes.includes(formatType);
}

export function compatibleFormatsForTemplate<TFormat>(
  template: unknown,
  formats: TFormat[],
): TFormat[] {
  if (!template) return formats;
  return formats.filter((format) => isTemplateCompatibleWithFormat(template, format));
}

export function pickBestCompatibleFormatForTemplate<TFormat>(
  template: unknown,
  formats: TFormat[],
): TFormat | null {
  const compatibleFormats = compatibleFormatsForTemplate(template, formats);
  if (compatibleFormats.length === 0) return null;

  const recommended = asRecommendedFormats(asRecord(template).recommendedFormats);
  const scored = compatibleFormats
    .map((format, index) => {
      const formatSource = asRecord(format);
      const formatType = String(formatSource.formatType ?? formatSource.id ?? '').trim();
      const recommendation = recommended.find((entry) => String(entry.formatType ?? '').trim() === formatType);
      const weight = typeof recommendation?.weight === 'number' ? recommendation.weight : 0;
      return { format, index, weight };
    })
    .sort((a, b) => b.weight - a.weight || a.index - b.index);

  return scored[0]?.format ?? compatibleFormats[0] ?? null;
}
